import { Video } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import RecordingItem, {
  makeRecordingItemProps,
} from '@/app/routes/app/modules/recording-item';
import { Drawer } from '@/components/ui';
import { useModulesData } from '@/features/modules/api/queries';
import SessionRecording from '@/features/modules/components/session-recording';
import { RecordingInfoPill } from '@/features/modules/components/session-recording/session-recording';
import type {
  OnDemandTopic,
  RecordableItem,
  Session,
} from '@/features/modules/types';
import { getSessionLabel, isTruthyOrZero } from '@/lib/utils';

import { useRecordingDrawer } from '../../hooks';

const SESSION_LABEL = getSessionLabel();

type RecordingDrawerProps = {
  onClose?: () => void;
};

export default function RecordingDrawer({
  onClose,
}: Readonly<RecordingDrawerProps>) {
  const {
    open,
    close,
    currentRecordingId,
    currentRecordingType,
    isOpen: isDrawerOpen,
  } = useRecordingDrawer();
  const [isOpen, setIsOpen] = useState(false);

  // Listen to recording drawer state and update local open state
  useEffect(() => {
    setIsOpen(isDrawerOpen);
  }, [isDrawerOpen]);

  // Get all sessions and on-demand modules data
  const { allSessions, onDemandModules, isLoading, getRelatedRecordings } =
    useModulesData();

  // Find the recording (session or topic) matching the ID from URL
  const currentRecording: Session | OnDemandTopic | null = useMemo(() => {
    if (!currentRecordingId || !currentRecordingType) return null;

    if (currentRecordingType === 'session') {
      return allSessions.find(s => s.id === currentRecordingId) ?? null;
    } else {
      // Find topic in on-demand modules
      for (const module of onDemandModules) {
        const topic = module.topics.find(t => t.id === currentRecordingId);
        if (topic) return topic;
      }
      return null;
    }
  }, [currentRecordingId, currentRecordingType, allSessions, onDemandModules]);

  // Get related recordings for recommendations
  const relatedRecordings: RecordableItem[] = useMemo(() => {
    if (!currentRecordingId || !currentRecordingType) return [];
    return getRelatedRecordings(currentRecordingId, currentRecordingType);
  }, [currentRecordingId, currentRecordingType, getRelatedRecordings]);

  // Handle drawer close - remove session_id query param
  const handleClose = () => {
    close();
    // Call optional onClose callback
    onClose?.();
  };

  const getSessionInfoPills = (recording: RecordableItem) => {
    const pills: RecordingInfoPill[] = [];
    if (
      recording.type === 'session' &&
      isTruthyOrZero(recording.sessionNumber)
    ) {
      pills.push({
        text: `${SESSION_LABEL.capitalize} ${recording.sessionNumber}`,
      });
    }
    return pills;
  };

  // Don't render if no recording ID in URL
  if (!currentRecordingId) {
    return null;
  }

  // Show loading state while fetching data
  if (isLoading) {
    return (
      <Drawer isOpen={isOpen} onClose={handleClose}>
        <div className="flex items-center justify-center p-8">
          <div className="text-center">Loading...</div>
        </div>
      </Drawer>
    );
  }

  return (
    <Drawer isOpen={isOpen} onClose={handleClose}>
      {currentRecording ? (
        <RecordingItem {...makeRecordingItemProps(currentRecording)} />
      ) : (
        <div className="flex flex-col items-center justify-center p-8 text-magical-moonlight-d1">
          <Video size={72} />
          <p className="font-bold text-center text-2xl">Recording not found!</p>
        </div>
      )}

      {/* Related Recordings Section */}
      {relatedRecordings.length > 0 && (
        <div className="px-4 pb-6">
          <h3 className="text-lg font-semibold text-magical-moonlight-d1 mb-4">
            Related Recordings
          </h3>
          <div className="flex flex-col lg:flex-row lg:flex-wrap gap-6">
            {relatedRecordings.map((recording, index) => (
              <SessionRecording
                key={recording.id}
                sessionTitle={recording.name}
                infoPills={getSessionInfoPills(recording)}
                moduleIcon={
                  recording.type === 'session'
                    ? recording.moduleDetails?.banner?.url
                    : undefined
                }
                onClick={() => open(recording.id, recording.type)}
                colorIndex={index}
              />
            ))}
          </div>
        </div>
      )}
    </Drawer>
  );
}
